/* ═══════════════════════════════════════════════════════════════════════════════
   PREPAID AMORTIZATION — shared straight-line cycle slicer
   ───────────────────────────────────────────────────────
   Single source of truth for slicing a per-cycle prepaid payment across its
   amortization months, rounding each month to cents and placing the accumulated
   remainder on the LAST month so the schedule sums to exactly the cycle total
   (CLAUDE.md §5 last-month-remainder rule).

   THE single slicer. Extracted in Path C Session 5f-pre sc-1 so runFullModel's
   forecast synth cycles slice identically to the stored-contract editor (no
   sub-cent drift between a synth cycle and an equivalent stored cycle). The prior
   engine code multiplied a per-cycle field by freqMonths and amortized it flat
   every month — 6× overstated for a semi-annual vendor, and drift-prone for odd
   cycle totals.

   CONVERGED (sc-4a): VendorDashboard's PrepaidModal now imports buildCycleAmort
   directly — the old component-local buildStraightLineAmort copy was deleted.
   This helper is the ONE slicer for both the engine forecast synth (runFullModel)
   AND the editor's single-contract + recurring-series (sc-4b) paths, so the two
   can no longer diverge.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { addMonths } from './dateUtils';

const r2 = n => Math.round(n * 100) / 100;

/* Slice `total` (a per-cycle payment) across `lengthMonths` consecutive months
   starting at `startMonth`. Each month gets r2(total / len) except the final
   month, which absorbs the rounding remainder (r2(total - cumulativePrior)) so
   the returned schedule sums to exactly `total`. Returns { "YYYY-MM": amount },
   or {} on invalid input. */
export function buildCycleAmort(startMonth, lengthMonths, total) {
  const len = Number(lengthMonths);
  const amt = Number(total) || 0;
  if (!startMonth || !Number.isFinite(len) || len <= 0) return {};
  const per = r2(amt / len);
  const amort = {};
  let cum = 0;
  for (let i = 0; i < len; i++) {
    const m = addMonths(startMonth, i);
    const v = i === len - 1 ? r2(amt - cum) : per;
    amort[m] = v;
    cum = r2(cum + v);
  }
  return amort;
}

/* Months between two "YYYY-MM" strings. */
const monthsBetween = (a, b) => { const [ay, am] = a.split('-').map(Number); const [by, bm] = b.split('-').map(Number); return (by - ay) * 12 + (bm - am); };

/* ★ THE FORECAST WINDOW — how many months the amortization actually has room in, on or after
   `firstForecast`. Extracted from buildForecastTail's own body (2026-08-14 hardening) so the
   editor can ASK the question the primitive answers, instead of re-deriving it. ONE rule, two
   readers (Principle 0): buildForecastTail branches on it, and PrepaidModal renders its
   "term has ended" notice from it. The arithmetic is verbatim what was inline before — no
   guards added, so the value is byte-identical for every input. `<= 0` means the term ended
   before the forecast begins, i.e. there is NO window to amortize into. */
export function forecastWindowLength(start, length, lag, firstForecast) {
  const lg = Number(lag) || 0;
  const amortEnd = addMonths(start, lg + Number(length) - 1);
  return monthsBetween(tailStartMonth(start, lg, firstForecast), amortEnd) + 1;
}

/* The month the forecast tail starts in: the lagged start, or firstForecast, whichever is later.
   Companion to forecastWindowLength — the same two lines both callers need, declared once so the
   window's START and its LENGTH cannot drift apart. */
function tailStartMonth(start, lg, firstForecast) {
  const lagStart = addMonths(start, lg);
  return lagStart > firstForecast ? lagStart : firstForecast;
}

/* FORECAST TAIL — the ONE tail (extracted from PrepaidModal VendorDashboard:747-756; the modal
   CALLS this — one matcher). Preserve the amort cells BEFORE firstForecast and re-spread the
   unamortized balance over the remaining forecast months.

   ★★ THE CONTRACT (rewritten 2026-08-14; the prior docstring claimed "Σ(result) == prepaid by
   construction" and that was FALSE — 382 measured violations. It is named here because that
   sentence is what let s2b route two more branches through this function without anyone
   re-deriving what it actually guarantees):

     · CONSERVATION IS THE DEFAULT, NOT THE LAW.
       Σ(result) == prepaid, EXCEPT where the asset is already consumed — see the floor.
     · EMPTY WINDOW  (forecastWindowLength <= 0, i.e. the term ended before firstForecast):
       the schedule is returned UNCHANGED. There is no formula-derived forecast to substitute,
       so substituting one would mean DESTROYING the only schedule that exists. This used to
       return `{ ...histKept }` — dropping the remainder AND any forecast cells the row carried
       — which silently zeroed $61,645 per tenant of Conferences event bookings (six rows whose
       whole amount sits in a single forecast cell while their nominal one-month term ended in
       the past). A lag edit, or Regenerate on its own, was enough to trigger it.
     · DIRECTIONAL FLOOR: `remaining` may not CROSS ZERO relative to the contract's own sign.
       An asset cannot be un-consumed and a credit cannot become a charge, so an over-booked
       history (Σ closed > prepaid) books $0 forward rather than a negative expense. ⛔ NOT
       `Math.max(0, remaining)`: a prepaid_amount can legitimately be NEGATIVE (a credit memo —
       two such contracts per live tenant), and for those a negative remaining is the CORRECT
       answer. A naive max() would silently delete a −$2,344.13 credit.
       ⇒ Where the floor fires, Σ(result) == Σ(closed) and the difference IS the over-booking.
         It is a GL fact to surface, not an error to hide; the caller may read it as
         `Σ(result) − prepaid`.
     · The window is NOT clipped to the model horizon: a term ending past `forecast_through`
       legitimately places cells beyond it (28 rows per live tenant already store such cells).
       Those months are never expensed by computeIS and never drained by the prepaid roll, so
       the asset correctly remains on the balance sheet at the horizon. Unchanged here.

   ⓘ THE BRANCH CONDITION IS UNCHANGED — this was `n > 0 ? tail : {}` and is now
   `if (!(n > 0)) return …`, the exact negation, so branch SELECTION is identical for every
   input (NaN included). Only what the empty-window branch RETURNS changed. */
export function buildForecastTail(priorAmort, prepaid, start, length, lag, firstForecast) {
  const p = priorAmort || {};
  const lg = Number(lag) || 0;
  const amt = Number(prepaid) || 0;
  const n = forecastWindowLength(start, length, lg, firstForecast);
  // EMPTY WINDOW → PRESERVE. Return a copy so the caller cannot alias the input.
  if (!(n > 0)) return { ...p };
  const histKept = Object.fromEntries(Object.entries(p).filter(([m]) => m < firstForecast));
  const bookedHist = r2(Object.values(histKept).reduce((s, v) => s + Number(v), 0));
  const rawRemaining = r2(amt - bookedHist);
  // DIRECTIONAL FLOOR — clamp toward zero on the contract's own side of zero.
  const remaining = amt >= 0 ? Math.max(0, rawRemaining) : Math.min(0, rawRemaining);
  const tail = buildCycleAmort(tailStartMonth(start, lg, firstForecast), n, remaining);
  return { ...histKept, ...tail };
}

/* ═══════════════════════════════════════════════════════════════════════════════
   BUILD 4c — PREPAID FORECAST AMORT AT IMPORT (clean-schedule generator).
   ───────────────────────────────────────────────────────────────────────────────
   C-2 shape: buildCycleAmort (above) is the engine/UI's ONE slicer; the onboarding
   import never ran it, so a truncated workbook (actuals only, no forward cells) imports
   with an EMPTY forecast amort and the prepaid forecast is blank. 4c fills it — CLIENT-
   computes, server persists verbatim (the wall: no server copy of this).

   ★ CLEAN-SCHEDULE (Finding 2): the forecast is a pure function of TERMS —
   buildCycleAmort(addMonths(start, derivedLag), length, prepaid) restricted to m > cutoff.
   It NEVER reads the historical cells for the forecast math, so it is robust to the
   template's missing close-month (measured: stored forecast == this, byte-identical). This
   is why 4c does NOT touch the PrepaidModal (no remaining-balance extraction, no
   convergence) — the modal's remaining-tail is for a COMPLETE-history lag edit; the import
   regenerates the clean schedule instead. Historical cells (≤ cutoff) pass through untouched.
   ═══════════════════════════════════════════════════════════════════════════════ */

/* Is this a TRUE event-style prepaid (Conferences — booked entirely in the event month, not
   straight-line)? PRECISE (0a): vendor 'Conferences' OR conference_name populated OR a real
   event_identifier — NEVER bare length===1 (that mis-flags legit one-month monthly prepaids
   like Intercom/Regus/Zoom). Event-style is EXCLUDED from generation (surfaced as a checklist). */
export function isPrepaidEventStyle(c) {
  return (c.vendor_name === 'Conferences')
    || (c.conference_name != null && String(c.conference_name).trim() !== '')
    || (c.event_identifier != null);
}

/* ★ 3a — THE STORED LAG MUST DESCRIBE THE STORED SCHEDULE.
   `amort_lag_months` is the offset between a contract's start_month and the month its
   amortization actually begins. It is NOT an independent input: whatever schedule ends up
   in `amort`, the lag that describes it is `firstAmortMonth − start_month`. Deriving it
   from the schedule we are about to persist makes the two agree BY CONSTRUCTION — they
   cannot drift, because one is read off the other.

   WHY THIS AND NOT `fillForecastPrepaidAmort`'s `lagUsed`: that value exists on exactly ONE
   of its six return paths (the generated one). A contract whose workbook already carried
   forward cells, whose history is irregular, whose term ended by the cutoff, or which is
   event-style returns no lag at all — and those are the majority. MEASURED against cid=3:
   `lagUsed` alone reproduces 0 of its 68 non-zero lags; this rule reproduces all 170
   contracts exactly, distribution included (0×102, 1×60, 2×1, 3×3, 5×1, 6×1, 7×1, 13×1).

   THE THREE ZERO CASES, each matching cid=3 (measured, not chosen):
     · EVENT-STYLE (Conferences): the single amort cell is the EVENT month, not a lagged
       start — reading it as a lag would invent 1-8 months of offset. cid=3 stores 0 on all
       19 of its event contracts.
     · zero amount or non-positive length: nothing is amortized; cid=3 stores 0.
     · no amort cells at all: nothing to describe.
   A negative offset (a schedule starting BEFORE start_month) floors at 0 — a lag is an
   offset forward, never backward.

   PURE. Reads only the contract. Never modifies the schedule. */
export function deriveAmortLag(contract) {
  const c = contract || {};
  if (isPrepaidEventStyle(c)) return 0;
  const len = Number(c.length_months), amt = Number(c.prepaid_amount);
  if (!(len > 0) || !(amt !== 0)) return 0;
  const keys = Object.keys(c.amort || {}).sort();
  if (!keys.length || !c.start_month) return 0;
  const d = monthsBetween(c.start_month, keys[0]);
  return d > 0 ? d : 0;
}

/* Derive the amort lag from a contract's OWN historical amort and verify the history is a CLEAN
   lagged straight-line — the imported historical keys must be the leading PREFIX of
   buildCycleAmort(start+lag, len, amt), values matching (tol $0.01). Returns { lag, clean }:
     clean === null  → no own history (a fresh forecast-start contract)
     clean === true  → history is a clean lagged straight-line → lag trustworthy
     clean === false → IRREGULAR history (catch-up / gap / out-of-order) → do NOT trust; flag.
   Robust to a truncated tail: the earliest key (→ lag) is unaffected, and the check needs only a
   PREFIX match, not the whole ≤cutoff schedule. */
function deriveOwnLag(c, cutoff) {
  const amort = c.amort || {};
  const len = Number(c.length_months), amt = Number(c.prepaid_amount);
  const histKeys = Object.keys(amort).filter(k => k <= cutoff).sort();
  if (!histKeys.length) return { lag: null, clean: null };
  const cand = monthsBetween(c.start_month, histKeys[0]);
  if (!(cand >= 0) || !(len > 0)) return { lag: 0, clean: false };
  // Clean = a lagged straight-line: the historical keys are a CONTIGUOUS prefix from start+cand,
  // AND each cell ≈ the flat per-month (prepaid/len) within $0.10. ★ Compare to `per`, NOT to
  // buildCycleAmort's cells: the engine puts the remainder on the LAST month, but the operator's
  // spreadsheet often amortizes EVENLY, so the last historical cell (when the history reaches the
  // term end) legitimately differs from the engine's remainder by a few cents — that must NOT read
  // as irregular. A catch-up LUMP (≥ 2×per) or a GAP (non-contiguous keys) still FAILS.
  const per = r2(amt / len);
  const clean = histKeys.length <= len
    && histKeys.every((k, i) => k === addMonths(c.start_month, cand + i) && Math.abs(r2(Number(amort[k])) - per) <= 0.10);
  return { lag: clean ? cand : 0, clean };
}

/* 4c generator. For an imported prepaid whose FORECAST cells are empty, fill the forward months to
   REPRODUCE the cat-2 remaining/tail (the stored oracle — the gate refuted a clean-full approach:
   the remainder placement differs). Returns { amort, generated, event, irregular, lagUsed }:
     event=true     → true conference; NOT generated (excluded; caller surfaces a checklist).
     irregular=true → history is not a clean straight-line; NOTHING generated (caller flags it).
     generated=true → amort = {...historical (untouched), ...forecast}.
   `siblings` = other parsed contracts (for the sibling lag carry when a fresh renewal has no own
   history — matched by vendor AND ACCOUNT: a vendor can have several contract lines with different
   lags, so vendor-name-only invents wrong lags; a name is not an identity). Guard: skip if a
   forecast cell already exists (never clobber a workbook that DID carry forward amort), len≤0,
   amount==0. ★ TRUNCATION-ROBUST: `booked` comes from the CLEAN schedule's ≤cutoff portion (via
   buildForecastTail on the clean schedule), NOT the truncated import — so the missing close-month
   cell can't skew the remaining. */
export function fillForecastPrepaidAmort(contract, cutoff, siblings) {
  const amort = contract.amort || {};
  const len = Number(contract.length_months), amt = Number(contract.prepaid_amount);
  if (isPrepaidEventStyle(contract)) return { amort, generated: false, event: true };
  if (Object.keys(amort).some(k => k > cutoff)) return { amort, generated: false }; // workbook supplied forecast — never clobber
  if (!(len > 0) || !(amt !== 0)) return { amort, generated: false };

  const own = deriveOwnLag(contract, cutoff);
  let lag = 0, lagAmbiguous = null;
  if (own.clean === false) return { amort, generated: false, irregular: true }; // messy history → flag, generate nothing
  if (own.clean === true) lag = own.lag;
  else {
    // Fresh renewal (no own history) → carry the predecessor lag, but ONLY if the same-(vendor,
    // account) siblings AGREE. A vendor can share ONE account across several contract lines with
    // DIFFERENT lags (Eshares/HUB), so (vendor,account) is not a unique identity — if the sibling
    // lags DISAGREE, do NOT guess: default lag=0 (amortizes sanely from start) and FLAG it (the
    // operator sets the amortization start in the Prepaid tab). A name is not an identity.
    const sibLags = new Set();
    for (const s of (siblings || [])) {
      if (s === contract || (s.vendor_name || '') !== (contract.vendor_name || '') || (s.account || '') !== (contract.account || '')) continue;
      const sl = deriveOwnLag(s, cutoff);
      if (sl.clean === true) sibLags.add(sl.lag);
    }
    if (sibLags.size === 1) lag = [...sibLags][0];               // unanimous → carry (may be 0)
    else if (sibLags.size > 1) { lag = 0; lagAmbiguous = [...sibLags].sort((a, b) => a - b); } // disagree → flag
    // size 0 → no clean sibling → lag stays 0 (a clean fresh contract with no predecessor lag)
  }

  const firstForecast = addMonths(cutoff, 1);
  const termEnd = addMonths(contract.start_month, lag + len - 1);
  if (termEnd <= cutoff) return { amort, generated: false }; // term ended by cutoff → no forecast

  // Reproduce cat-2's remaining/tail via the SHARED buildForecastTail, with `booked` from the CLEAN
  // schedule (truncation-robust). Handles FRESH (start>cutoff → histKept empty → full clean) and
  // TAIL (start≤cutoff → remaining over the forward months) uniformly. Take only the > cutoff cells
  // and merge with the imported historical (preserved untouched).
  const clean = buildCycleAmort(addMonths(contract.start_month, lag), len, amt);
  const tailFull = buildForecastTail(clean, amt, contract.start_month, len, lag, firstForecast);
  const forecast = {};
  for (const [m, v] of Object.entries(tailFull)) if (m > cutoff) forecast[m] = v;
  if (!Object.keys(forecast).length) return { amort, generated: false };
  return { amort: { ...amort, ...forecast }, generated: true, lagUsed: lag, lagAmbiguous };
}

/* ★★ THE PREPAID ANCHOR LEAF — ONE RESOLVER, TWO CALLERS.
 *
 * The forecast balance sheet re-seats prepaid from the GL balance at the close month
 * (`runFullModel`'s mandatory re-seat, e89fb9e). Finding that balance means knowing WHICH BS
 * leaf carries it, and the answer is per-tenant: `computeBS.js:113` resolves it from
 * `line.module_link_ref`, the wiring the operator established at Step 4b. Reading it any other
 * way is a guess.
 *
 * ⛔ THIS EXISTS SO THERE IS EXACTLY ONE ANSWER. It was inline in `runFullModel` for one
 * commit; the onboarding pre-flight is about to assert the SAME condition the engine enforces,
 * and a second copy of this rule would let the CHECK and the GUARD resolve different leaves —
 * the check going green while the engine throws, or the reverse. §6 Principle 0: a parallel
 * implementation does not merely repeat code, it guarantees drift, and drift between a guard
 * and the check that verifies it is the worst place to allow it.
 *
 * ⓘ THE LITERAL FALLBACK IS DELIBERATE, not laziness: a legacy caller passing the hardcoded
 * BS_LINES has no `module_link_ref` on its leaves (the same reason `LEGACY_MODULE_LINK_REF`
 * exists in computeBS), and this must not become a place such a caller silently loses its
 * anchor. Pure function of its argument — it makes no assumption about whether the skeleton
 * has been through `injectDynamicBSLines`, because its two callers demonstrably pass different
 * arrays (the pre-flight gets the INJECTED one, the engine the un-injected one) and injection
 * never touches the prepaid leaf. */
export function resolvePrepaidAnchorLeafId(bsLines) {
  return (Array.isArray(bsLines) ? bsLines.find(l => l && l.module_link_ref === 'prepaid_balance') : null)?.id
    || 'prepaid_expenses';
}

/* ===============================================================================
   PREPAID RENEWALS -- O2 (Brian 2026-10-29; diffs/prepaid-renewals-check.txt Part 3, diffs/prepaid-renews-p1-build.txt)
   -------------------------------------------------------------------------------
   A contract's `renews` answer: NULL = not answered = NO renewal (every row stored before this build) | true = it renews |
   false = answered, it does not renew. A renewing contract MATERIALIZES its successors as ordinary stored prepaid_contracts
   rows: the engine reads them like any other contract and does not change. A successor copies its predecessor's amount,
   length and lag, starts at predecessor start + predecessor length, and is itself renews = true, so the chain runs through
   forecast_through (a successor is generated only if its START <= forecast_through; its schedule is built in full, like the
   recurring series). A successor's renewal_of_id names its IMMEDIATE predecessor.
   EDITED vs GENERATED is decided by COMPARISON with what the generator would have written -- no marker (the W-013 pattern,
   CLAUDE.md 12.5 rule 102). Every function below is pure: no I/O.
   REFS: a row's identity in these functions is prepaidRef(row) -- 'id:<id>' for a stored row, else its client_key. A
   generated row carries client_key '<seed ref>#<start month>' (unique: starts rise along a chain) and renewal_of_key = its
   predecessor's ref; the server turns the keys into renewal_of_id (the Step-6 import maps them after insert).
   =============================================================================== */

/* THE CYCLE PRIMITIVE -- one contract cycle as a stored row. EXTRACTED from PrepaidModal's "Recurring series" (handleGenerate),
   which now calls it, so the series and the renewal generator build a cycle the SAME way (field order included). The caller
   passes values already normalised (trimmed names, an r2 amount, a numeric lag), exactly as the modal did inline. */
export function prepaidCycleRow({ vendorName, vendorId, account, isLine, start, lengthMonths, amount, lag }) {
  const amort = buildCycleAmort(lag > 0 ? addMonths(start, lag) : start, lengthMonths, amount);
  return {
    vendor_name: vendorName,
    vendor_id: vendorId,
    account,
    is_line: isLine || null,
    start_month: start,
    length_months: lengthMonths,
    prepaid_amount: amount,
    amort,
    amort_lag_months: deriveAmortLag({
      vendor_name: vendorName,
      conference_name: null,
      event_identifier: null,
      start_month: start,
      length_months: lengthMonths,
      prepaid_amount: amount,
      amort,
    }),
    event_identifier: null,
    event_month: null,
  };
}

/* A contract's terms in ONE shape, whichever shape it arrives in: the stored / POST shape (vendor_name, start_month, ...),
   the GET shape (vendor, start, length, prepaid, vendorId, amortLagMonths) or the onboarding row. */
export function prepaidContractTerms(c) {
  const o = c || {};
  const pick = (...ks) => { for (const k of ks) if (o[k] !== undefined) return o[k]; return undefined; };
  const len = pick('length_months', 'length');
  const renews = pick('renews');
  return {
    vendor_name: pick('vendor_name', 'vendor') ?? null,
    vendor_id: pick('vendor_id', 'vendorId') ?? null,
    account: pick('account') ?? null,
    is_line: pick('is_line') || null,
    start_month: pick('start_month', 'start') || null,
    length_months: len == null || len === '' ? null : Number(len),
    prepaid_amount: r2(Number(pick('prepaid_amount', 'prepaid')) || 0),
    amort_lag_months: Number(pick('amort_lag_months', 'amortLagMonths')) || 0,
    amort: pick('amort') || {},
    event_identifier: pick('event_identifier', 'eventIdentifier') ?? null,
    event_month: pick('event_month', 'eventMonth') || null,
    renews: renews === true ? true : renews === false ? false : null,
  };
}

/* A contract's ref in these functions: 'id:<id>' when stored, else its client_key (null when it has neither). */
export function prepaidRef(c) {
  if (!c) return null;
  if (c.id != null) return 'id:' + c.id;
  return c.client_key != null ? String(c.client_key) : null;
}

/* Why a contract cannot seed a renewal (null = it can). An event booking is not a renewal (isPrepaidEventStyle -- the import's
   own event rule -- or a stored event month); a contract with no length cannot repeat; a zero amount renews nothing. */
export function prepaidRenewRefusal(c) {
  const t = prepaidContractTerms(c);
  if (!t.start_month) return 'no-start-month';
  if (!(t.length_months > 0)) return 'no-length';
  if (isPrepaidEventStyle({ vendor_name: t.vendor_name, conference_name: (c || {}).conference_name ?? null, event_identifier: t.event_identifier }) || t.event_month) return 'event-booking';
  if (!(t.prepaid_amount !== 0)) return 'zero-amount';
  return null;
}

/* The ONE successor rule: what the generator writes after `pred` -- its amount, length and lag, starting at pred start + pred
   length -- as a renewing cycle row. null when pred cannot renew. */
export function nextPrepaidRenewal(pred) {
  if (prepaidRenewRefusal(pred)) return null;
  const t = prepaidContractTerms(pred);
  const row = prepaidCycleRow({
    vendorName: t.vendor_name, vendorId: t.vendor_id, account: t.account, isLine: t.is_line,
    start: addMonths(t.start_month, t.length_months), lengthMonths: t.length_months, amount: t.prepaid_amount, lag: t.amort_lag_months,
  });
  row.renews = true;
  return row;
}

/* The database's own collision key (prepaid_contracts_unique: company, vendor_name, start_month, length_months,
   prepaid_amount, event_identifier, NULLS NOT DISTINCT) -- the vendor name EXACT, as the index compares it. */
export function prepaidCollisionKey(c) {
  const t = prepaidContractTerms(c);
  return [t.vendor_name, t.start_month, t.length_months == null ? 'NULL' : t.length_months, t.prepaid_amount.toFixed(2), t.event_identifier ?? 'NULL'].join('|');
}

/* Are two rows the same cycle, term for term? The comparison that tells a GENERATED row from an EDITED one. The renews answer
   and the links are not terms. */
export function prepaidSameTerms(a, b) {
  const x = prepaidContractTerms(a), y = prepaidContractTerms(b);
  for (const k of ['vendor_name', 'vendor_id', 'account', 'is_line', 'start_month', 'length_months', 'prepaid_amount', 'amort_lag_months', 'event_identifier', 'event_month']) {
    if ((x[k] ?? null) !== (y[k] ?? null)) return false;
  }
  const ka = Object.keys(x.amort).sort(), kb = Object.keys(y.amort).sort();
  if (ka.length !== kb.length) return false;
  return ka.every((k, i) => k === kb[i] && r2(Number(x.amort[k])) === r2(Number(y.amort[k])));
}

const RENEWAL_GUARD = 1200;   // a chain longer than this is a defect, not a forecast (100 years of monthly cycles)

/* GENERATE a renewing seed's successors through forecastThrough. `existing` = the company's other contracts: a generated row
   whose collision key one of them already holds is SKIPPED and REPORTED, and the chain STOPS there (generating past it would
   hang later cycles off a contract this chain does not own). Returns { rows, refused, collision }. */
export function generatePrepaidRenewals(seed, opts) {
  const o = opts || {};
  const forecastThrough = o.forecastThrough;
  const seedRef = o.seedRef || prepaidRef(seed);
  if (!forecastThrough) return { rows: [], refused: 'no-forecast-through', collision: null };
  if (prepaidContractTerms(seed).renews !== true) return { rows: [], refused: 'renews-not-true', collision: null };
  const refusal = prepaidRenewRefusal(seed);
  if (refusal) return { rows: [], refused: refusal, collision: null };
  const taken = new Map();
  for (const e of (o.existing || [])) if (e && e !== seed) taken.set(prepaidCollisionKey(e), e);
  const rows = [];
  let pred = seed, predRef = seedRef, collision = null;
  for (let k = 0; k < RENEWAL_GUARD; k++) {
    const next = nextPrepaidRenewal(pred);
    if (!next || next.start_month > forecastThrough) break;
    const hit = taken.get(prepaidCollisionKey(next));
    if (hit) { collision = { start_month: next.start_month, with: prepaidRef(hit) }; break; }
    next.client_key = seedRef + '#' + next.start_month;
    next.renewal_of_key = predRef;
    rows.push(next);
    pred = next; predRef = next.client_key;
  }
  return { rows, refused: null, collision };
}

/* RECONCILE a chain after an edit. root = the chain's first contract (its current state); descendants = its stored successors
   in chain order (each one's predecessor is the one before it; the first's is root); before = { [ref]: the row's image BEFORE
   this edit } for every row edited in this operation. A descendant is UNEDITED when it equals what the generator writes from
   its predecessor's BEFORE image. An unedited row is REGENERATED from the current chain: kept as is when that changes nothing,
   updated in place otherwise, removed when it would now start after forecastThrough. An EDITED row is KEPT, and the rows after
   it regenerate FROM IT. After the last stored row the chain continues through forecastThrough (a collision stops it, reported).
   Returns { keep, update: [{ ref, row }], create: [row], remove, edited, collision, refused }. */
export function reconcilePrepaidRenewalChain(params) {
  const p = params || {};
  const root = p.root, descendants = p.descendants || [], before = p.before || {}, forecastThrough = p.forecastThrough;
  const out = { keep: [], update: [], create: [], remove: [], edited: [], collision: null, refused: null };
  if (!root || !forecastThrough) { out.refused = !root ? 'no-root' : 'no-forecast-through'; return out; }
  if (prepaidContractTerms(root).renews !== true) { out.refused = 'renews-not-true'; return out; }
  const rootRef = prepaidRef(root);
  const chainRefs = new Set([rootRef, ...descendants.map(prepaidRef)]);
  let cur = root, curRef = rootRef, active = true;
  for (let i = 0; i < descendants.length; i++) {
    const d = descendants[i], ref = prepaidRef(d);
    const prevStored = i === 0 ? root : descendants[i - 1];
    const prevOld = before[prepaidRef(prevStored)] || prevStored;
    const expectedOld = nextPrepaidRenewal(prevOld);
    const unedited = !!expectedOld && prepaidSameTerms(d, expectedOld);
    if (!unedited) { out.edited.push(ref); out.keep.push(ref); cur = d; curRef = ref; active = prepaidContractTerms(d).renews === true; continue; }
    const expectedNew = active ? nextPrepaidRenewal(cur) : null;
    if (!expectedNew || expectedNew.start_month > forecastThrough) { out.remove.push(ref); continue; }
    if (prepaidSameTerms(d, expectedNew)) { out.keep.push(ref); cur = d; curRef = ref; continue; }
    out.update.push({ ref, row: { ...expectedNew, renewal_of_key: curRef } });
    cur = expectedNew; curRef = ref;
  }
  if (active) {
    const taken = new Map();
    for (const e of (p.existing || [])) { const r = prepaidRef(e); if (!(r && chainRefs.has(r))) taken.set(prepaidCollisionKey(e), e); }
    for (let k = 0; k < RENEWAL_GUARD; k++) {
      const next = nextPrepaidRenewal(cur);
      if (!next || next.start_month > forecastThrough) break;
      const hit = taken.get(prepaidCollisionKey(next));
      if (hit) { out.collision = { start_month: next.start_month, with: prepaidRef(hit) }; break; }
      next.client_key = rootRef + '#' + next.start_month;
      next.renewal_of_key = curRef;
      out.create.push(next);
      cur = next; curRef = next.client_key;
    }
  }
  return out;
}

/* RENEWS TURNED OFF on `row`: its UNEDITED successors are removed; its EDITED successors are KEPT and returned flagged (the
   Part-2 screen shows them for the operator to decide). A kept row whose predecessor is removed is relinked to its nearest
   kept ancestor (relinkTo -- possibly `row` itself). Classification is by comparison with each successor's stored
   predecessor, the same test the reconcile uses. Returns { remove, keep, flagged: [{ ref, relinkTo }] }. */
export function planPrepaidRenewsOff(params) {
  const p = params || {};
  const row = p.row, descendants = p.descendants || [];
  const out = { remove: [], keep: [], flagged: [] };
  let keptAncestor = prepaidRef(row);
  let prevRemoved = false;
  for (let i = 0; i < descendants.length; i++) {
    const d = descendants[i], ref = prepaidRef(d);
    const pred = i === 0 ? row : descendants[i - 1];
    const expected = nextPrepaidRenewal(pred);
    const unedited = !!expected && prepaidSameTerms(d, expected);
    if (unedited) { out.remove.push(ref); prevRemoved = true; continue; }
    out.keep.push(ref);
    out.flagged.push({ ref, relinkTo: prevRemoved ? keptAncestor : null });
    keptAncestor = ref; prevRemoved = false;
  }
  return out;
}

/* THE SUGGESTION ("suggested -- confirm or change"): a chain-tail rule, per contract, with its reason. It may be wrong -- the
   stored answer is the operator's (SubBase's ABC Builders vendor holds two contracts that BOTH renew; the rule switches the
   earlier one off). Rules, in order:
     OFF  a one-month or indefinite contract (length <= 1 or none) | an event booking | a zero amount
     OFF  superseded: a later contract of the same vendor, the same line and the same length starts after it and no later
          than its renewal month
     OFF  its renewal month is on or before the close and no renewal is on file (it ran its course)
     ON   otherwise -- the chain tail
   Same vendor = the same vendor_id when both carry one, else the same name (case / spaces ignored); same line = the same
   is_line when both carry one, else the same account. Returns [{ ref, suggest, reason }] in input order. */
export function suggestPrepaidRenews(contracts, opts) {
  const o = opts || {};
  const cutoff = o.cutoff || null;
  const list = (contracts || []).map(c => ({ c, t: prepaidContractTerms(c) }));
  const norm = s => String(s == null ? '' : s).trim().toLowerCase();
  const sameVendor = (a, b) => (a.vendor_id != null && b.vendor_id != null) ? a.vendor_id === b.vendor_id : norm(a.vendor_name) === norm(b.vendor_name);
  const sameLine = (a, b) => (a.is_line && b.is_line) ? norm(a.is_line) === norm(b.is_line) : norm(a.account) === norm(b.account);
  return list.map(({ c, t }) => {
    const ref = prepaidRef(c);
    if (!(t.length_months > 1)) return { ref, suggest: false, reason: 'a one-month or indefinite contract' };
    const refusal = prepaidRenewRefusal(c);
    if (refusal === 'event-booking') return { ref, suggest: false, reason: 'an event booking' };
    if (refusal) return { ref, suggest: false, reason: refusal === 'zero-amount' ? 'a zero amount' : refusal };
    const renewalMonth = addMonths(t.start_month, t.length_months);
    const by = list.find(x => x.c !== c && sameVendor(x.t, t) && sameLine(x.t, t) && x.t.length_months === t.length_months
      && x.t.start_month > t.start_month && x.t.start_month <= renewalMonth);
    if (by) return { ref, suggest: false, reason: 'superseded by the ' + by.t.start_month + ' contract' };
    if (cutoff && renewalMonth <= cutoff) return { ref, suggest: false, reason: 'its renewal month ' + renewalMonth + ' is on or before the close and no renewal is on file' };
    return { ref, suggest: true, reason: 'the chain tail: renews ' + renewalMonth };
  });
}
